<template>
    <MainContent title="Importaciones">
        <template v-slot:header>
            <div class="d-flex justify-content-between align-items-center w-100 mb-3">
                <h3 class="card-title pt-2 mb-0">Nueva importación</h3>
            </div>
        </template>
        <template v-slot:content>
            <Stepper :steps="steps" ref="stepper">
                <template v-slot:paso-tipo>
                    <b-row>
                        <b-col cols="12">
                            <h4 class="mb-4">Selecciona el tipo de importación</h4>
                            <div class="form-group">
                                <label for="tipoImportacion">Tipo de importación</label>
                                <select 
                                    id="tipoImportacion"
                                    v-model="tipoImportacion" 
                                    class="form-control"
                                    @change="resetImportData"
                                >
                                    <option value="">Selecciona una opción</option>
                                    <option value="csv">Archivo CSV</option>
                                    <option value="textarea">Texto plano</option>
                                </select>
                                <small class="form-text text-muted">
                                    Selecciona si quieres importar desde un archivo o pegando texto directamente
                                </small>
                            </div>
                        </b-col>
                        <b-col cols="12" class="text-right mt-4">
                            <b-button 
                                variant="primary" 
                                @click="goToNextStep"
                                :disabled="!tipoImportacion"
                            >
                                Continuar
                                <i class="fas fa-arrow-right ml-2"></i>
                            </b-button>
                        </b-col>
                    </b-row>
                </template>

                <template v-slot:paso-datos>
                    <b-row>
                        <b-col cols="12">

                            <div v-if="tipoImportacion === 'csv'">
                                <h4 class="mb-4">Selecciona el archivo a importar</h4>
                                <DropzoneFileUploader
                                    :acceptedFiles="'.csv,.xlsx,.xls'"
                                    :maxFiles="1"
                                    :maxFilesize="10"
                                    message="Arrastra tu archivo CSV aquí, o haz clic para seleccionarlo"
                                    @file-added="handleFileUpload"                                
                                    ref="dropzoneUploader"
                                />
                            </div>

                            <div v-if="tipoImportacion === 'textarea'">
                                <h4 class="mb-4">Pega el texto a importar</h4>
                                <div class="form-group">
                                    <label for="textoImportacion">Texto de importación</label>
                                    <textarea 
                                        id="textoImportacion"
                                        v-model="textoImportacion"
                                        class="form-control"
                                        rows="10"
                                        placeholder="Pega aquí el texto con los datos de los subscriptores...Email, Nombre, Apellidos, Teléfono"
                                    ></textarea>
                                    <small class="form-text text-muted">
                                        Pega el texto con los datos de los subscriptores. Cada línea debe contener los datos de un subscriptor.
                                        <br/>
                                        Líneas detectadas: <strong>{{ contarLineas }}</strong>
                                    </small>
                                </div>
                            </div>
                        </b-col>
                        <b-col cols="12" class="text-right mt-4">
                            <b-button variant="outline-secondary" class="mr-2" @click="goBackStep">
                                <i class="fas fa-arrow-left mr-2"></i>
                                Volver
                            </b-button>
                            <b-button 
                                variant="primary" 
                                @click="goToNextStep"
                                :disabled="!isDatosValid"
                            >
                                Continuar
                                <i class="fas fa-arrow-right ml-2"></i>
                            </b-button>
                        </b-col>
                    </b-row>
                </template>

                <template v-slot:paso-grupo>
                    <b-row>
                        <b-col cols="12">
                            <h4 class="mb-4">Selecciona el grupo al que pertenecerán los subscriptores</h4>
                            <div class="form-group">
                                <label>Grupo</label>
                                <multiselect 
                                    v-model="selectedGroup" 
                                    :options="grupos" 
                                    :multiple="false"
                                    :close-on-select="true"
                                    placeholder="Seleccionar un grupo" 
                                    label="nombre" 
                                    track-by="id"
                                    :loading="loadingGrupos"
                                >
                                    <template slot="noOptions">
                                        {{ loadingGrupos ? 'Cargando grupos...' : 'No hay grupos disponibles' }}
                                    </template>
                                </multiselect>
                                <small class="form-text text-muted">
                                    Selecciona el grupo al que pertenecerán los subscriptores importados
                                </small>
                            </div>
                            
                            <div class="alert alert-info mt-3">
                                <h5><i class="fas fa-info-circle mr-2"></i>Resumen de importación</h5>
                                <p><strong>Tipo:</strong> {{ tipoImportacion === 'csv' ? 'Archivo CSV' : 'Texto plano' }}</p>
                                <p v-if="tipoImportacion === 'csv' && selectedFile">
                                    <strong>Archivo:</strong> {{ selectedFile.name }}
                                </p>
                                <p v-if="tipoImportacion === 'textarea' && textoImportacion">
                                    <strong>Líneas de texto:</strong> {{ contarLineas }}
                                </p>
                                <p v-if="selectedGroup">
                                    <strong>Grupo destino:</strong> {{ selectedGroup.nombre }}
                                </p>
                            </div>
                        </b-col>
                        <b-col cols="12" class="text-right mt-4">
                            <b-button variant="outline-secondary" class="mr-2" @click="goBackStep">
                                <i class="fas fa-arrow-left mr-2"></i>
                                Volver
                            </b-button>
                            <b-button 
                                variant="primary" 
                                @click="handleImportacion"
                                :disabled="!selectedGroup || isProcessing"
                            >
                                <span v-if="isProcessing">
                                    <i class="fas fa-spinner fa-spin mr-2"></i>Procesando...
                                </span>
                                <span v-else>
                                    Finalizar importación
                                </span>
                            </b-button>
                        </b-col>
                    </b-row>
                </template>
            </Stepper>
        </template>
    </MainContent>
</template>

<script>
    import DropzoneFileUploader from "@/components/common/DropZoneFileUploader.vue";
    import MainContent from "@/components/backend/components/MainContent";
    import Stepper from '@/components/frontend/events/compra/components/Stepper';
    import apiService from '@/services/api-comunicados'

    export default {
        
        components: {
            DropzoneFileUploader,
            MainContent,
            Stepper
        },
        computed: {
            steps() {
                return [
                    { label: this.$t('campana_emails.subir_archivos'), slot: 'paso-archivo' },
                    { label: this.$t('campana_emails.seleccionar_grupo'), slot: 'paso-grupo' }
                ]
            }
        },
        data() {
            return {
                fileUploaded: false,
                selectedFile: null,
                fileUploadError: null,
                loadingGrupos: false,
                isProcessing: false,
                grupos: [],
                selectedGroup: null
            };
        },
        methods: {
            goToNextStep() {
                if (this.fileUploaded && this.selectedFile) {
                    this.$refs.stepper.changeStep();
                } else {
                    this.$toast.error("Debes seleccionar un archivo válido.");
                }
            },
            goBackStep() {
                this.$refs.stepper.changeStep(true);
            },

            handleFileUpload(file) {
                this.fileUploadError = null;
                if (!file) {
                    this.selectedFile = null;
                    this.fileUploaded = false;
                    return;
                }
                if (!file.name) {
                    this.fileUploadError = "El formato del archivo no es válido";
                    this.fileUploaded = false;
                    this.selectedFile = null;
                    return;
                }
                const fileName = file.name.toLowerCase();
                const validExtensions = ['.csv', '.xlsx', '.xls'];
                const hasValidExtension = validExtensions.some(ext => 
                    fileName.endsWith(ext) || 
                    fileName.endsWith(ext.substring(1)) 
                );
                
                if (!hasValidExtension) {
                    this.fileUploadError = "Solo se permiten archivos CSV o Excel (.csv, .xlsx, .xls)";
                    this.fileUploaded = false;
                    this.selectedFile = null;
                    return;
                }
                this.selectedFile = file;
                this.fileUploaded = true;
            },
            async getGrupos() {
                this.loadingGrupos = true;
                try {
                    const response = await apiService.getGrupos({ to_select: true });
                    this.grupos = response.data;
                } catch (error) {
                    this.$toast.error('Error al cargar los grupos');
                } finally {
                    this.loadingGrupos = false;
                }
            },
            async handleImportacion() {
                if (!this.selectedGroup || !this.selectedFile) {
                    this.$toast.error('Debes subir un archivo y seleccionar un grupo.');
                    return;
                }
                this.isProcessing = true;
                try {
                    const formData = new FormData();
                    formData.append('import_file', this.selectedFile);
                    formData.append('grupo_id', this.selectedGroup.id);
                    const response = await apiService.importarSubscriptores(formData);          
                    const successMessage = response && response.data && response.data.message 
                        ? response.data.message 
                        : 'Importación iniciada con éxito';              
                    this.$toast.success(successMessage);
                    this.$router.push({ name: 'back.emails.importaciones' });
                } catch (error) { 
                    let errorMsg = 'Error al finalizar la importación';
                    if (error && error.response) {
                        if (error.response.data && error.response.data.message) {
                            errorMsg = error.response.data.message;
                        } else if (error.response.data && error.response.data.errors) {
                            const errors = error.response.data.errors;
                            const firstError = Object.values(errors)[0];
                            errorMsg = Array.isArray(firstError) ? firstError[0] : firstError;
                        }
                    } else if (error && error.message) {
                        errorMsg = error.message;
                    }
                    
                    this.$toast.error(errorMsg);
                } finally {
                    this.isProcessing = false;
                }
            }
        },
        created() {
            this.getGrupos();
        }
    };
</script>

<style scoped>
.alert ul {
    padding-left: 20px;
    margin-top: 5px;
}

h4 {
    color: #495057;
}

</style>